/**
 * Helper functions for widget store
 **/
import { isEmpty } from "lodash";

// helper function to convert api response to layout format
export const convertToDisplay = (data, rootGetters, admin = false) => {
  const returnData = [];
  let removeDoubleQuot;
  data.forEach((row) => {
    if (row.properties && row.properties.length > 0) {
      // stupid issue where uuids were double quoted, need to find where it has come from
      removeDoubleQuot = row.properties[0].value.replace(
        /''([\w\-]*)''/g,
        "'$1'"
      );
      const elements = JSON.parse(removeDoubleQuot.replace(/'/g, '"'));

      let structureRow = [];

      elements.cols.forEach((element) => {
        const cssClass = elements.layout;
        let props = element.props;

        // if there are no props saved, grab props from the rootGetters
        if (!props || props.length < 1 || Object.keys(props).length < 1) {
          const widgetType = element.type.split("-")[0]; // news-widget -> news
          props = rootGetters[`${widgetType}/getProps`];
        }
        structureRow.push({
          type: element.type,
          props: props,
          cssClass,
          admin: element.admin === "true" && element.type !== "", // don't set admin for empty spots
          uuid: element.uuid,
        });
      });

      returnData.push(structureRow);
    }
  });

  return returnData;
};

/**
 * Filter widgets for a specific area out of main payload
 * @param {*} data The data to check against (usually dashboard or admin dashboard)
 * @param {*} label Which section to filter out (headerRow/mainRow/footerRow)
 * @param {*} rootGetters Reference to global rootGetters object
 * @param {*} isAdmin Determine whether to mark widgets as admin or not
 */
export const filterWidgets = (data, label, rootGetters, isAdmin = false) => {
  if (!data || (data && data.length < 1)) {
    return { raw: {}, compiled: [] };
  }
  const widgets = data.filter((el) => el.type === label);

  let compiled = [];
  let raw = null;

  if (widgets.length > 0) {
    compiled = trimLayout(convertToDisplay(widgets, rootGetters, isAdmin));
    raw = JSON.parse(widgets[0].properties[0].value.replace(/'/g, '"'));
  }
  return {
    raw,
    compiled,
  };
};

/**
 * Merge admin and user widgets together
 * @param {Object []} adminWidgets - An object with admin widgets
 * @param {Object []} userWidgets - An object with user widgets
 * @param {Boolean false} adminPriority - Whenther to prioritize admin or user
 * @returns {Object []} The objects merged together in an array
 */
export const mergeWidgets = (
  adminWidgets,
  userWidgets,
  adminPriority = true
) => {
  // first thing to do is create a layout that matches the admin layout
  let newLayout = [];
  let rowCount = 0;
  let userUuids = [];
  let adminUuids = [];

  // get a list of all the admin uuids first
  if (adminWidgets.compiled && adminWidgets.compiled.length > 0) {
    adminWidgets.compiled.forEach((row) => {
      row.forEach((col) => {
        if (col.uuid !== "") {
          adminUuids.push(col.uuid);
        }
      });
    });
  }

  // Loop through user widgets, reference them, update any admins that are not admins any more
  // TODO: Only list those that are marked as admin?
  if (userWidgets.compiled && userWidgets.compiled.length > 0) {
    userWidgets.compiled.forEach((row) => {
      row.forEach((col) => {
        userUuids.push(col.uuid);
      });
    });
  }

  if (!adminPriority && userWidgets.compiled.length > 0) {
    /**
     * This is for the main/user section bit
     *
     * Basically, we need to read everything just from the user dashboard call
     * We need to check first  if the elements that are marked as admin are still admin ones
     * Update to false if not
     * Then if there are any elements in the admin section that AREN'T being displayed, we need to append them
     */

    // Loop through user widgets, reference them, update any admins that are not admins any more
    // TODO: Only list those that are marked as admin?
    if (userWidgets.compiled && userWidgets.compiled.length > 0) {
      userWidgets.compiled.forEach((row) => {
        let newRow = row;
        newRow.forEach((col) => {
          if (col.admin && !adminUuids.includes(col.uuid)) {
            col.admin = false;
          }
        });
        newLayout.push(newRow);
      });
    }

    // now compare the two type arrays and see if there is anything left in admin that needs adding to the user
    let adminEls = adminUuids.filter((type) => !userUuids.includes(type));

    /**
     * If there are any admin elements that have been added since the user last saved their dashboard,
     * we need to add them in here
     *
     * Also, we need to try and break them up into rows of a maximum of 3, but they should have CSS classes
     * attached to them
     */
    if (!isEmpty(adminEls)) {
      let remainingWidgets = [];
      adminWidgets.compiled.forEach((row) => {
        row.forEach((col) => {
          if (adminEls.includes(col.uuid)) {
            remainingWidgets.push(col);
          }
        });
      });

      let newRow = [];
      let count = 0;
      remainingWidgets.forEach((widget, index) => {
        newRow.push(widget);
        count += widget.cssClass;

        // if we now have no widgets left, see if we need any spaces, push the row
        if (index + 1 == remainingWidgets.length) {
          if (count == 6) {
            newRow.push({
              cssClass: 6,
              type: "",
              admin: false,
              uuid: "",
            });
          } else if (count == 4) {
            newRow.push(
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              },
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              }
            );
          }
          newLayout.push(newRow);
          newRow = [];
          count = 0;
        } else if (
          remainingWidgets[index + 1] &&
          remainingWidgets[index + 1].cssClass + count > 12
        ) {
          if (count == 6) {
            newRow.push({
              cssClass: 6,
              type: "",
              admin: false,
              uuid: "",
            });
          } else if (count == 4) {
            newRow.push(
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              },
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              }
            );
          }
          newLayout.push(newRow);
          newRow = [];
          count = 0;
        }
      });
    }

    return newLayout;
  } else {
    /**
     * This is for the header / footer bits
     */
    if (adminWidgets.compiled && adminWidgets.compiled.length > 0) {
      // Loop through the admin widgets
      adminWidgets.compiled.forEach((row, rowIndex) => {
        let currentRow = [];

        row.forEach((col, colIndex) => {
          // if there is an empty space, see if the user has a block in it's place
          if (row[colIndex].type == "") {
            if (
              userWidgets.compiled &&
              userWidgets.compiled[rowIndex] &&
              userWidgets.compiled[rowIndex][colIndex] &&
              userWidgets.compiled[rowIndex][colIndex].type !== ""
            ) {
              currentRow[colIndex] = {
                ...userWidgets.compiled[rowIndex][colIndex],
                cssClass: col.cssClass, // need to change the css class so that it matches the admin one
                admin: false, // remove the admin flag
              };

              // remove the uuid from user widgets so it isn't duplicated later
              userUuids = userUuids.filter(
                (uuid) => uuid != userWidgets.compiled[rowIndex][colIndex].uuid
              );
            } else {
              // so that the user can add stuff
              currentRow[colIndex] = {
                ...adminWidgets.compiled[rowIndex][colIndex],
                admin: false,
              };
            }
          } else {
            currentRow[colIndex] = adminWidgets.compiled[rowIndex][colIndex];
          }
        });
        newLayout.push(currentRow);
        rowCount++;
      });
    } else {
      // TODO: What if the user has header/footer widgets but there is no admin ones left?
      // newLayout.push([]);
    }
    // we then should loop through the rest of the user rows if they exist
    // if any widgets have been removed from admin then they should be editable now, or removed at least
    // we also need to somehow make sure that they all have their proper css class too
    const remainingUuids = userUuids.filter(
      (uuid) => !adminUuids.includes(uuid) && uuid != ""
    );

    if (!isEmpty(remainingUuids)) {
      let remainingWidgets = [];
      userWidgets.compiled.forEach((row) => {
        row.forEach((col) => {
          if (remainingUuids.includes(col.uuid)) {
            col.admin = false;
            remainingWidgets.push(col);
          }
        });
      });

      let newRow = [];
      let count = 0;
      remainingWidgets.forEach((widget, index) => {
        newRow.push(widget);
        count += widget.cssClass;

        // if we now have no widgets left, see if we need any spaces, push the row
        // also try to handle if the next element isn't the same class
        if (remainingWidgets[index + 1] && remainingWidgets[index + 1].cssClass !== widget.cssClass) {
          if (count == 6) {
            newRow.push({
              cssClass: 6,
              type: "",
              admin: false,
              uuid: "",
            });
          } else if (count == 4) {
            newRow.push(
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              },
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              }
            );
          } else if (count == 8) {
            newRow.push({
              cssClass: 4,
              type: "",
              admin: false,
              uuid: "",
            });
          }
          newLayout.push(newRow);
          newRow = [];
          count = 0;
        } else if (index + 1 == remainingWidgets.length) {
          if (count == 6) {
            newRow.push({
              cssClass: 6,
              type: "",
              admin: false,
              uuid: "",
            });
          } else if (count == 4) {
            newRow.push(
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              },
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              }
            );
          } else if (count == 8) {
            newRow.push({
              cssClass: 4,
              type: "",
              admin: false,
              uuid: "",
            });
          }
          newLayout.push(newRow);
          newRow = [];
          count = 0;
        } else if (
          remainingWidgets[index + 1] &&
          remainingWidgets[index + 1].cssClass + count > 12
        ) {
          if (count == 6) {
            newRow.push({
              cssClass: 6,
              type: "",
              admin: false,
              uuid: "",
            });
          } else if (count == 4) {
            newRow.push(
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              },
              {
                cssClass: 4,
                type: "",
                admin: false,
                uuid: "",
              }
            );
          } else if (count == 8) {
            newRow.push({
              cssClass: 4,
              type: "",
              admin: false,
              uuid: "",
            });
          }
          newLayout.push(newRow);
          newRow = [];
          count = 0;
        }
      });
    }

    return newLayout;
  }
};

/**
 * Fast UUID generator, RFC4122 version 4 compliant.
 * @author Jeff Ward (jcward.com).
 * @license MIT license
 * @link http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
 **/
export const UUID = (function() {
  var self = {};
  var lut = [];
  for (var i = 0; i < 256; i++) {
    lut[i] = (i < 16 ? "0" : "") + i.toString(16);
  }
  self.generate = function() {
    var d0 = (Math.random() * 0xffffffff) | 0;
    var d1 = (Math.random() * 0xffffffff) | 0;
    var d2 = (Math.random() * 0xffffffff) | 0;
    var d3 = (Math.random() * 0xffffffff) | 0;
    return (
      lut[d0 & 0xff] +
      lut[(d0 >> 8) & 0xff] +
      lut[(d0 >> 16) & 0xff] +
      lut[(d0 >> 24) & 0xff] +
      "-" +
      lut[d1 & 0xff] +
      lut[(d1 >> 8) & 0xff] +
      "-" +
      lut[((d1 >> 16) & 0x0f) | 0x40] +
      lut[(d1 >> 24) & 0xff] +
      "-" +
      lut[(d2 & 0x3f) | 0x80] +
      lut[(d2 >> 8) & 0xff] +
      "-" +
      lut[(d2 >> 16) & 0xff] +
      lut[(d2 >> 24) & 0xff] +
      lut[d3 & 0xff] +
      lut[(d3 >> 8) & 0xff] +
      lut[(d3 >> 16) & 0xff] +
      lut[(d3 >> 24) & 0xff]
    );
  };
  return self;
})();

/**
 * Check rows for a section and remove any that are completely empty
 * Empty rows can be 2 things - an empty row with no widgets, or a row with 'empty' widgets
 * @param {*} layout
 */
export const trimLayout = (layout) => {
  let returnRows = [];

  // filter out any rows with nothing at all
  const newLayout = layout.filter((row) => row.length > 0);

  // loop through the remaining rows and remove any that are all empty widgets
  let thisrow;
  let rowsToRemove = [];
  newLayout.forEach((row, index) => {
    thisrow = row.filter((col) => col.type != "");
    if (thisrow.length == 0) {
      rowsToRemove.push(index);
    }
  });

  return newLayout.filter((row, index) => !rowsToRemove.includes(index));
};
